import React from 'react';
import { Plugin } from '@devexpress/dx-react-core';
import { RowDetailState } from '@devexpress/dx-react-grid';
import { TableRowDetail } from '@devexpress/dx-react-grid-material-ui';
import { useTheme } from '@mui/material';
import { DetailColumnLast } from './DetailColumnLast';
import { TableStateContext } from '../TableStateContext';

/**
 * The expand chevron's column, in the px dev-express wants.
 *
 * <p>Left at dev-express's own default on purpose. `TableFixedColumns` reserves this much at the
 * right edge for the toggle whatever `toggleColumnWidth` says, so narrowing the column only moves
 * the chevron and leaves the reserved gap behind -- measured on the underwriting inbox, the space
 * between the pinned Action column and the toggle went 105px at 48 and 93px at 36, when the
 * intent was for it to close entirely.
 *
 * <p>Exported because `BaseTable` reserves it for the fill calculation and carried the same 48
 * written out twice, which is one edit away from columns that overflow their container.
 */
export const ROW_DETAIL_TOGGLE_WIDTH = 48;

/**
 * The toggle column's width, always stated. dev-express's wrapper applies its 48 as
 * `{ toggleColumnWidth: 48, ...props }`, so passing undefined replaced it and the column had no
 * width at all: it swallowed a fixed table's spare width, and on a fill table it fell back to the
 * 120 minimum and pushed the table past its container.
 */
export const rowDetailToggleWidth = (inlineToggle?: boolean): number =>
  inlineToggle ? 0 : ROW_DETAIL_TOGGLE_WIDTH;

export const RowDetailPlugin = ({ ...props }) => {
  // @ts-expect-error TS(2339): Property 'state' does not exist on type 'unknown'.
  const { state, handlers, ...rest } = React.useContext(TableStateContext);
  return <RowDetailPluginBase {...props} {...state} {...handlers} {...rest} />;
};

export const RowDetailPluginBase = ({ ...props }) => {
  if (!props.rowDetailComponent) {
    throw new Error();
  }
  return (
    <Plugin>
      <RowDetailState
        expandedRowIds={props.expandedRowIds}
        onExpandedRowIdsChange={props.onExpandedRowIdsChange}
      />
      <TableRowDetail
        contentComponent={props.rowDetailComponent}
        cellComponent={DetailCell}
        toggleCellComponent={props.inlineToggle ? EmptyToggleCell : ToggleCell}
        toggleColumnWidth={rowDetailToggleWidth(props.inlineToggle)}
        {...props.tableRowDetailProps}
      />
      <DetailColumnLast />
    </Plugin>
  );
};

/**
 * The chevron's own cell.
 *
 * <p>`cellComponent` is the expanded row's container, spanning every column -- not this. Styling
 * that one to tidy the chevron restyles the expanded panel on every listing and leaves the toggle
 * exactly as it was, which is how the chevron ended up squeezed: dev-express's 8 and 15 of padding
 * around a 32px icon do not fit the column, so it overflowed to the right and opened a gap on its
 * left. The padding goes here, where the chevron actually lives.
 */
const ToggleCell = (props: any) => (
  <TableRowDetail.ToggleCell
    {...props}
    style={{ paddingLeft: 0, paddingRight: 0, textAlign: 'center' }}
  />
);

/**
 * No chevron, for a listing that renders its own inside the Action cell.
 *
 * <p>The column cannot be removed -- `TableRowDetail` always contributes one -- so it is collapsed
 * to nothing instead. A bare `td`, not `TableRowDetail.ToggleCell`: that renders its own chevron
 * whatever children it is given, which is two chevrons on a row that already has one in its Action
 * cell. `getReservedChromeWidth` reserves nothing in this case either, or the fill calculation
 * would hold back width for a column that is no longer there.
 */
const EmptyToggleCell = () => (
  <td style={{ padding: 0, width: 0, border: 0 }} />
);

const DetailCell = (props: any) => {
  const { palette } = useTheme();
  return (
    <TableRowDetail.Cell
      {...props}
      style={{ backgroundColor: palette.background.tableBody }}
    />
  );
};

export default RowDetailPluginBase;
